import { Injectable } from '@angular/core';
import { Task, TaskForm } from '../modules/go/model/task.model';
import { BehaviorSubject, catchError, defaultIfEmpty, filter, forkJoin, map, mergeMap, Observable, of, switchMap, take, tap, throwIfEmpty, withLatestFrom } from 'rxjs';
import { Key, NgxIndexedDBService } from 'ngx-indexed-db';
import { AttachmentEntity, FormEntity, TaskDataEntity, TaskEntity } from '../config/dbstorage.config';
import {
  ControlValue,
  FailureDescription,
  FormEntry,
  FormEntryValue,
  FormValue,
  getFormEntryCloneIndex,
  getFormEntryInstanceKey
} from '../modules/go/model/form.model';
import { Attachment, AttachmentType } from '../model/file.model';
import imageResize from 'image-resize';
import { ConnectionStatusService } from './connection-status.service';
import { getDistance } from 'ol/sphere';
import { HISTORICAL_FAILURES_LOCATION_RADIUS_METERS } from '../config/general.config';


@Injectable({
  providedIn: 'root'
})
export class OfflineService {

  private offlineDataChanged_$ = new BehaviorSubject<void>(undefined)
  public offlineDataChanged$ = this.offlineDataChanged_$.asObservable();

  constructor(
    private _dbService: NgxIndexedDBService,
    private _connectionStatusService: ConnectionStatusService
  ) { }

  private safeDbRead<T>(source$: Observable<T>, fallbackValue: T, operation: string): Observable<T> {
    return source$.pipe(
      catchError(error => {
        console.error(`[OfflineService] IndexedDB read failed during ${operation}.`, error);
        return of(fallbackValue);
      })
    );
  }

  private extractId<T extends { id?: Key }>(entity: T | string | number | null | undefined): number | null {
    if ((entity ?? null) == null) {
        return null;
    }

    if (typeof entity === 'number') {
        return entity;
    }

    if (typeof entity === 'string') {
        const parsedId = Number(entity);
        return isNaN(parsedId) ? null : parsedId;
    }

    return entity?.id !== undefined ? Number(entity.id) : null;
  }

  private createFormId(formId: Key, formVersion?: Key): Key {
    return formId + ((formVersion ?? null) === null ? '' : 'v' + formVersion);
  }
  private reportOfflineDataChanged(){
    this.offlineDataChanged_$.next();
  }

  deleteAttachment(entity: number | AttachmentEntity | string | null | undefined): Observable<boolean> {
    const id = this.extractId(entity);
    if(id !== null) {
      return this._dbService.deleteByKey('ATTACHMENTS', id!)
    }
    return of(true);
  }

  deleteTaskAttachments(entity: number | {id: number} | null | undefined): Observable<boolean[]> {
    const id = this.extractId(entity);
    if(id){
      return this.fetchAttachments(id)
      .pipe(
        switchMap(attachments => {
          if(attachments.length){
            return forkJoin(attachments.map(att => this.deleteAttachment(att)))
          }
          return of([true]);
        }),
      )
    }
    return of([true]);
  }

  deleteTaskData(entity: number | TaskDataEntity | null | undefined): Observable<boolean> {
    const id = this.extractId(entity);
    if(id !== null) {
      return this.fetchTaskData('', id!)
      .pipe(
        switchMap(taskData =>  {
          if(taskData){
            return this._dbService.deleteByKey('TASK_DATA', taskData.id!)
          }
          return of(true);
        })
      )
    }
    return of(true);
  }

  deleteTask(entity: number | TaskEntity | null | undefined): Observable<boolean> {
    const id = this.extractId(entity);//(entity ?? null) === null ? null : (typeof entity === 'number' ? entity : entity!.id);
    if(id !== null) {
      return this.fetchTask('', id!)
      .pipe(
        switchMap(task =>  {
          if(task){
            return this._dbService.deleteByKey('TASKS', task.id!)
          }
          return of(true);
        }),
        tap(() => this.reportOfflineDataChanged())
      )
    }
    return of(true);
  }

  fetchAttachment(id: IDBValidKey) {
    return this._dbService.getByKey<AttachmentEntity>('ATTACHMENTS', id)
  }

  fetchAttachmentByAttachmentId(id: IDBValidKey) {
    return this._dbService.getByIndex<AttachmentEntity>('ATTACHMENTS', 'attachmentId', id)
  }

  fetchAttachments(taskId: Key): Observable<AttachmentEntity[]> {
    return this.safeDbRead(
      this._dbService.getAllByIndex<AttachmentEntity>('ATTACHMENTS', 'taskId', IDBKeyRange.only(String(taskId))),
      [],
      `fetchAttachments(${String(taskId)})`
    );
  }
  fetchAllAttachments(): Observable<AttachmentEntity[]> {
    return this.safeDbRead(
      this._dbService.getAll<AttachmentEntity>('ATTACHMENTS'),
      [],
      'fetchAllAttachments'
    );
  }
  fetchTaskData(projectId: string, taskId: Key): Observable<TaskDataEntity | null> {
    return this.safeDbRead(
      this._dbService.getByIndex<TaskDataEntity>('TASK_DATA', 'taskId', String(taskId)),
      null,
      `fetchTaskData(${String(taskId)})`
    );
  }
  fetchAllTaskData(projectId: string): Observable<TaskDataEntity[]> {
    return this.safeDbRead(
      this._dbService.getAllByIndex<TaskDataEntity>('TASK_DATA', 'projectId', IDBKeyRange.only(String(projectId))),
      [],
      `fetchAllTaskData(${projectId})`
    );
  }
  fetchTask(projectId: string, taskId: Key): Observable<TaskEntity | null> {
    return this.safeDbRead(
      this._dbService.getByIndex<TaskEntity>('TASKS', 'taskId', String(taskId)),
      null,
      `fetchTask(${String(taskId)})`
    );
  }

  fetchTasks(projectId: String): Observable<TaskEntity[]> {
    return this.safeDbRead(
      this._dbService.getAllByIndex<TaskEntity>('TASKS', 'projectId', IDBKeyRange.only(projectId)),
      [],
      `fetchTasks(${String(projectId)})`
    );
  }

  fetchForm(projectId: string, formId: Key, formVersion?: Key): Observable<FormEntity | null> {
    return this._dbService.getByIndex<FormEntity>('FORMS', 'templateFormId_projectId', [String(this.createFormId(formId, formVersion)), projectId])
  }


  getTask(projectId: string, taskId: Key ): Observable<Task | null> {
    //? pobieramy ew. Task, pobieramy ew. zapisane TaskData

    //? Jeśli jesteśmy offline:
    //? łączymy te dane ze sobą i odsyłamy całość jako Task, niezależnie od tego czy istnieją TaskData

    //? Jeśli jesteśmy online:
    //? Sprawdzamy czy mamy Task i TaskData.
    //? Jeśli są obydwa, łączymy je i odsyłamy jako Task (dane powstały i nie zostały zsynchronizowane, więc to są te najbardziej aktualne)
    //? Jeśli nie ma któregoś z nich, usuwamy obydwa z lokalnej bazy i zwracamy null
    //?   (Task wtedy pobierzemy online, a same dane bez taska to najwyraźniej jakieś śmieci)

    return forkJoin({
      task: this.fetchTask(projectId, taskId),
      taskData: this.fetchTaskData(projectId, taskId),
      attsCount: this.safeDbRead(
        this._dbService.countByIndex('ATTACHMENTS', "taskId", String(taskId)),
        0,
        `countAttachments(${String(taskId)})`
      ),
    })
    .pipe(
      withLatestFrom(this._connectionStatusService.networkStatus$),
      switchMap(([{task, taskData, attsCount}, isOnline]) => {
        if(!isOnline || (task && (taskData || attsCount > 0))) {
          if(task){
            task.payload.localVersion = true;
          }
          return of(this.merge(task?.payload, taskData?.payload));
        }
        else {
          // W tym miejscu jesteśmy online, i nie mamy taska i załączników lub danych offlineowych taska. Można bezpiecznie usunąć ew. pozostałości. Task i tak zostanie pobrany online
          return forkJoin([
            this.deleteTask(task),
            this.deleteTaskData(taskData)
          ]).pipe(
            map(() => null)
          )
        }
      }),
      map(task => task)
    )
  }

  private merge(task?: Task, taskData?: FormValue): Task | null {
    if(!task) {
      return null;
    }
    if(!taskData) {
      return task;
    }

    if (taskData.blocked !== undefined) {
      task.blocked = taskData.blocked;
    }

    taskData?.formEntries.forEach(entry => this.updateEntry(entry, task.form!.entries));

    return task ?? null;
  }

  private updateEntry(entryValue: FormEntryValue, entries: FormEntry[]) {
    const entryInstanceKey = getFormEntryInstanceKey(entryValue.id, entryValue.cloneIndex);
    const formEntry = entries.find(formEntry =>
      getFormEntryInstanceKey(formEntry.templateFormEntryId, getFormEntryCloneIndex(formEntry.cloneIndex)) === entryInstanceKey
    );
    if(!formEntry){
      return;
    }
    formEntry.failureDescription = entryValue.failureDescription;

    entryValue.values?.forEach(value => this.updateControlValue(formEntry, value))
    entryValue.children?.forEach(child => this.updateEntry(child, formEntry.children!))
  }

  private updateControlValue(formEntry: FormEntry, value: ControlValue) {
    const control = formEntry.control?.find(entry => entry.templateFormControlId === value.id);
    if(control){
      control.value = value.value;
    }
  }

  updateTask(task: Task, projectId: string): Observable<Task>{
    return this._dbService.getByIndex<TaskEntity>('TASKS', 'taskId', String(task.id))
    .pipe(
      switchMap(entity => {
        const update: TaskEntity = {taskId: String(task.id), projectId, payload: task};
        if(entity) {
          update.id = entity.id
        }
        return this._dbService.update('TASKS', update).pipe(
          map((taskSchema) => taskSchema.payload)
        )
      }),
      tap(() => this.reportOfflineDataChanged())
    )
  }

  updateForm(projectId: string, form: TaskForm): Observable<TaskForm> {
    const formId = String(this.createFormId(form.templateFormId, form.templateFormVersion));
    return this._dbService.getByIndex('FORMS', 'templateFormId_projectId', [formId, projectId])
    .pipe(
      switchMap(entity => {
        const update: FormEntity = {templateFormId: formId, projectId, payload: form};
        if(entity) {
          update.id = (entity as any).id
        }
        return this._dbService.update('FORMS', update).pipe(
          map((taskSchema) => taskSchema.payload)
        )
      })
    )
  }

  saveTaskData(projectId: string, taskId: IDBValidKey, formEntries: FormValue, isDraft: boolean): Observable<Task | null> {
    return this._dbService.getByIndex<TaskDataEntity>('TASK_DATA', 'taskId', taskId)
      .pipe(
        switchMap(value => {
          if(value) {
            return this._dbService.update<TaskDataEntity>('TASK_DATA', {id: (value as any).id, taskId, projectId, payload: formEntries, isDraft})
          }
          return this._dbService.update('TASK_DATA', {taskId, projectId, payload: formEntries, isDraft});
        }),
        tap(() => this.reportOfflineDataChanged()),
        switchMap(() => this.getTask(projectId, taskId))
      )
  }

  async createThumbnail(imageFile: File): Promise<string> {
    return await imageResize(imageFile, {
      width: 160,
      height: 120,
      outputType: 'base64'
    }) as string;
  }

  private convertToEntity(file: File, metadata: Map<string, any>, taskId: string): Observable<AttachmentEntity> {
    const fileToFormData = (file: File, metadata: Map<string, any>) => {
      const newData = new FormData();
      newData.append('files', file);
      [...metadata].forEach(([key, value]) => newData.append(key, value))
      return newData;
    }

    return forkJoin({
      taskId: of(taskId),
      thumbnail: this.createThumbnail(file),
      payload: of([...fileToFormData(file, metadata)])
    })
  }

  private extractMetadata(data: FormData): Map<string, any> {
    const metaData = new Map<string, any>();
    [...data]
    .filter(([key]) => key !== 'files')
    .forEach(([key, value]) => metaData.set(key, value))
    return metaData;
  }

  saveAttachments(data: FormData, type: AttachmentType, taskId: number): Observable<Attachment[]> {
    const metadata = this.extractMetadata(data);
    return forkJoin(
      data.getAll('files').map(file => this.convertToEntity(file as File, metadata, String(taskId)))
    )
    .pipe(
      switchMap(fileEntities => this._dbService.bulkAdd('ATTACHMENTS', fileEntities)),
      switchMap(ids => this._dbService.bulkGet<AttachmentEntity[]>('ATTACHMENTS', ids) as Observable<Required<AttachmentEntity>[]>),
      mergeMap(fileEntities => this.fakeUploadResponses(fileEntities, type)),
      tap(() => this.reportOfflineDataChanged())
    );
  }

  saveAttachmentEntity(attachments: AttachmentEntity | AttachmentEntity[], type: AttachmentType): Observable<Attachment[]> {
    if(!Array.isArray(attachments)) {
      attachments = [attachments];
    }
    return of(attachments)
    .pipe(
      switchMap(fileEntities => this._dbService.bulkAdd('ATTACHMENTS', fileEntities)),
      switchMap(ids => this._dbService.bulkGet<AttachmentEntity[]>('ATTACHMENTS', ids) as Observable<Required<AttachmentEntity>[]>),
      mergeMap(fileEntities => this.fakeUploadResponses(fileEntities, type)),
      tap(() => this.reportOfflineDataChanged())
    )
  }

  saveFailures(failures: FailureDescription[]) {
    return failures && failures.length ? this._dbService.bulkPut<FailureDescription>('FAILURES', failures) : of(null);
  }

  private resolveUpperDateBound(toDate?: string | null): { upperDate: string; upperOpen: boolean } {
    if (!toDate) {
      return { upperDate: new Date().toISOString(), upperOpen: false };
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(toDate)) {
      const nextDay = new Date(`${toDate}T00:00:00.000Z`);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      return { upperDate: nextDay.toISOString(), upperOpen: true };
    }

    return { upperDate: toDate, upperOpen: false };
  }

  private getLatitudeRange(latitude: number): { minLatitude: number; maxLatitude: number } {
    const latitudeDelta = HISTORICAL_FAILURES_LOCATION_RADIUS_METERS / 111_320;
    return {
      minLatitude: latitude - latitudeDelta,
      maxLatitude: latitude + latitudeDelta
    };
  }

  private isWithinRadius(failure: FailureDescription, latitude: number, longitude: number): boolean {
    return getDistance([failure.longitude, failure.latitude], [longitude, latitude]) <= HISTORICAL_FAILURES_LOCATION_RADIUS_METERS;
  }

  private isTowerFailure(failure: FailureDescription): boolean {
    return Object.prototype.hasOwnProperty.call(failure, 'externalId');
  }

  private getFailuresByLocation(latitude: number, longitude: number, fromDate: string, toDate?: string | null): Observable<FailureDescription[]> {
    const { upperDate, upperOpen } = this.resolveUpperDateBound(toDate);
    const { minLatitude, maxLatitude } = this.getLatitudeRange(latitude);
    const range = IDBKeyRange.bound([minLatitude, fromDate], [maxLatitude, upperDate], false, upperOpen);

    return this.safeDbRead(
      this._dbService.getAllByIndex<FailureDescription>('FAILURES', 'byLatitude_Date', range),
      [],
      `getFailuresByLocation(${latitude}, ${longitude}, ${fromDate}, ${toDate ?? ''})`
    ).pipe(
      map(results => results.filter(failure => this.isWithinRadius(failure, latitude, longitude)))
    );
  }

  getTowerFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate?: string | null): Observable<FailureDescription[]> {
    return this.getFailuresByLocation(latitude, longitude, fromDate, toDate).pipe(
      map(results => results.filter(failure =>
        this.isTowerFailure(failure) &&
        failure.failureCodes?.some(code => codes.includes(code.code))
      ))
    );
  }

  getSpanFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate?: string | null): Observable<FailureDescription[]> {
    return this.getFailuresByLocation(latitude, longitude, fromDate, toDate)
    .pipe(
      map(results => results.filter(failure => failure.failureCodes?.some(code => codes.includes(code.code))))
    );
  }
  private fakeUploadResponses(fileEntities: Required<AttachmentEntity>[], type: AttachmentType) {
    return fileEntities.map((fileEntity) => fileEntity.payload
          .filter(elem => elem[0] === 'files')
          .map(([_, file]) => this.fakeUploadResponse(fileEntity, file.name, type))
        )
  }
  private fakeUploadResponse(fileEntity: Required<AttachmentEntity>, fileName: string, type: AttachmentType): Attachment {
    return {
      id: -fileEntity.id, // Ujemny identyfikator znaczy, że mamy do czynienia z lokalną wersją
      originalFilename: fileName,
      type: type,
      thumbnail: fileEntity.thumbnail
    };
  }

  getAttachmentFile(id: number): Observable<Blob> {
    return this.fetchAttachment(id)
    .pipe(
      filter(fileEntity => !!(fileEntity?.payload ?? false)),
      throwIfEmpty(() => new Error("ERRORS.ATTACHMENT_NOT_FOUND")),
      map(fileEntity => fileEntity.payload.find(entry => entry[0] === 'files')?.[1]),
      map(file => file as Blob)
    )
  }

  getAttachmentFileByAttachmentId(id: number): Observable<Blob> {
    return this.fetchAttachmentByAttachmentId(id)
    .pipe(
      filter(fileEntity => !!(fileEntity?.payload ?? false)),
      throwIfEmpty(() => new Error("ERRORS.ATTACHMENT_NOT_FOUND")),
      map(fileEntity => fileEntity.payload.find(entry => entry[0] === 'files')?.[1]),
      map(file => file as Blob)
    )
  }

  getAttachmentFormData(id: number): Observable<FormData | null> {
    return this.fetchAttachment(id)
    .pipe(
      filter(fileEntity => !!(fileEntity?.payload ?? false)),
      map(fileEntity => {
        const formData = new FormData();
        fileEntity.payload.forEach(([key, value]) => formData.append(key, value))
        return formData;
      })
    )
  }

  attachmentExists(id: number): Observable<boolean>{
    return this._dbService.getByIndex('ATTACHMENTS', 'attachmentId', id).pipe(
      map(result => !!result),
      catchError((error) => {console.log(error); return of(false)})
    );
  }

  hasOfflineData(taskId: string | number | TaskEntity): Observable<boolean> {
    const id = typeof(taskId) === 'number' || typeof(taskId) === 'string' ? taskId : taskId.taskId!;
    return forkJoin({
      data: this.safeDbRead(
        this._dbService.getByIndex<TaskDataEntity | null>('TASK_DATA', "taskId", String(id)),
        null,
        `hasOfflineData.data(${String(id)})`
      ),
      atts: this.safeDbRead(
        this._dbService.getByIndex<AttachmentEntity | null>('ATTACHMENTS', "taskId", String(id)),
        null,
        `hasOfflineData.atts(${String(id)})`
      ),
    }).pipe(
      map(({data, atts}) => !!data || !!atts)
    )
  }

  hasProjectOfflineData(projectId: string): Observable<boolean> {
    return this.fetchTasks(projectId)
    .pipe(
      mergeMap(tasks => tasks),
      mergeMap(task => this.hasOfflineData(task)),
      filter(hasOfflineData => hasOfflineData),
      defaultIfEmpty(false),
      take(1)
    )
  }

}
